/*
 * HTML pins for the admin members map.
 *
 * The legacy google.maps.Marker can only show an image URL, which is why
 * members used to appear as bare 36px squares. An OverlayView lets the pin be
 * real DOM - round photo, ring, tail, status dot, hover card - styled from
 * map.component.scss, with no Cloud Console map ID required. A map ID would
 * also switch off the JSON map style in MAP_STYLE below.
 *
 * google.maps is loaded at runtime, so the overlay class is built on first use
 * rather than at module load.
 */
import { MapMember, MapSpace } from '../data-transfer-objects/member-map.dto';

export interface HtmlOverlay {
  setMap(map: google.maps.Map | null): void;
}

type HtmlOverlayCtor = new (
  position: google.maps.LatLngLiteral,
  element: HTMLElement
) => HtmlOverlay;

let overlayCtor: HtmlOverlayCtor | undefined;

function htmlOverlayClass(): HtmlOverlayCtor {
  if (overlayCtor) return overlayCtor;

  class HtmlPinOverlay extends google.maps.OverlayView {
    constructor(
      private readonly position: google.maps.LatLngLiteral,
      private readonly element: HTMLElement
    ) {
      super();
      // Without this a click on a pin also reaches the map, which would drop
      // a search point underneath it.
      google.maps.OverlayView.preventMapHitsAndGesturesFrom(element);
    }

    override onAdd(): void {
      this.getPanes()?.overlayMouseTarget.appendChild(this.element);
    }

    override draw(): void {
      const point = this.getProjection()?.fromLatLngToDivPixel(
        new google.maps.LatLng(this.position)
      );
      if (!point) return;
      this.element.style.left = `${point.x}px`;
      this.element.style.top = `${point.y}px`;
    }

    override onRemove(): void {
      this.element.remove();
    }
  }

  overlayCtor = HtmlPinOverlay;
  return overlayCtor;
}

export function createHtmlOverlay(
  position: google.maps.LatLngLiteral,
  element: HTMLElement
): HtmlOverlay {
  const Ctor = htmlOverlayClass();
  return new Ctor(position, element);
}

/* ---------- status ---------- */

/** Seen this recently counts as online. Presence is polled, not pushed. */
export const ONLINE_WINDOW_MS = 15 * 60 * 1000;
/**
 * "New" follows the map's timeframe: joined within the window the admin is
 * looking at. This is the fallback when there is no window ("Dawn of time").
 */
export const NEW_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

/** A timeframe option (1h, 12h, 1d, 7d, 30d) in milliseconds; null for none. */
export function timeframeMs(value: string): number | null {
  const m = parseTimeframe(value);
  return m ? m.n * (m.unit === 'h' ? 3_600_000 : 86_400_000) : null;
}

/** A timeframe option in words: "hour", "12 hours", "day", "7 days"; 14 days when none. */
export function timeframeWords(value: string): string {
  const m = parseTimeframe(value);
  if (!m) return '14 days';
  const word = m.unit === 'h' ? 'hour' : 'day';
  return m.n === 1 ? word : `${m.n} ${word}s`;
}

function parseTimeframe(value: string): { n: number; unit: 'h' | 'd' } | null {
  const m = /^(\d+)([hd])$/.exec(value ?? '');
  return m ? { n: Number(m[1]), unit: m[2] as 'h' | 'd' } : null;
}

export function isOnline(m: Pick<MapMember, 'lastSeen'>, now = Date.now()): boolean {
  return !!m.lastSeen && now - Date.parse(m.lastSeen) <= ONLINE_WINDOW_MS;
}

export function isNew(
  m: Pick<MapMember, 'createdAt'>,
  now = Date.now(),
  windowMs = NEW_WINDOW_MS
): boolean {
  return !!m.createdAt && now - Date.parse(m.createdAt) <= windowMs;
}

/** "just now", "12 min ago", "3 h ago", "5 d ago", "4 mo ago". */
export function timeAgo(isoDate: string | null, now = Date.now()): string | null {
  if (!isoDate) return null;
  const minutes = Math.max(0, Math.round((now - Date.parse(isoDate)) / 60000));
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 60) return `${days} d ago`;
  return `${Math.round(days / 30)} mo ago`;
}

/** Great-circle distance in km. */
export function distanceKm(a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

export function distanceLabel(km: number): string {
  return km < 1 ? `${Math.round(km * 1000)} m away` : `${Math.round(km).toLocaleString()} km away`;
}

export function initialsOf(user: { firstName?: string | null; lastName?: string | null }): string {
  const first = user.firstName?.trim()?.[0] ?? '';
  const last = user.lastName?.trim()?.[0] ?? '';
  return (first + last).toUpperCase() || '?';
}

function nameOf(m: MapMember): string {
  return `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || 'Member';
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/** Round photo, or initials when there is none or it fails to load. */
function face(m: MapMember, className: string): HTMLElement {
  const box = el('span', className);
  const showInitials = () => {
    box.replaceChildren();
    box.classList.add('nx-initials');
    box.textContent = initialsOf(m);
  };
  if (m.photo) {
    const img = document.createElement('img');
    img.src = m.photo;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.referrerPolicy = 'no-referrer';
    // LinkedIn and expired storage URLs 403; fall back rather than show a
    // broken-image glyph.
    img.addEventListener('error', showInitials, { once: true });
    box.appendChild(img);
  } else {
    showInitials();
  }
  return box;
}

/**
 * Open the hover card below its pin when there is no room above it, since the
 * map frame clips anything that pokes out of the top.
 */
function placeCard(pin: HTMLElement, card: HTMLElement): void {
  const frame = pin.closest('.map-frame');
  if (!frame) return;
  const room = pin.getBoundingClientRect().top - frame.getBoundingClientRect().top;
  card.classList.toggle('nx-card--below', room < card.offsetHeight + 16);
}

/* ---------- member pin ---------- */

export interface UserPinOptions {
  onClick: () => void;
  /** Filled in on hover, so it reflects the search point at that moment. */
  distance?: () => string | null;
  /** How recently joined counts as new; the map's timeframe. */
  newWithinMs?: number;
}

/**
 * A member pin: round photo in a ring with a tail on the exact spot, a green
 * dot when online, a lighter ring when new, and a card on hover.
 */
export function buildUserPin(m: MapMember, opts: UserPinOptions): HTMLElement {
  const name = nameOf(m);
  const pin = el('button', 'nx-pin');
  pin.type = 'button';
  pin.setAttribute('aria-label', name);
  const fresh = isNew(m, Date.now(), opts.newWithinMs);
  if (fresh) pin.classList.add('nx-pin--new');

  const ring = el('span', 'nx-pin__ring');
  ring.appendChild(face(m, 'nx-pin__face'));
  if (isOnline(m)) ring.appendChild(el('span', 'nx-pin__online'));

  const card = el('span', 'nx-card');
  card.appendChild(el('span', 'nx-card__name', name));
  const work = [m.role, m.company].filter((s) => s && s.trim()).join(' · ');
  if (work) card.appendChild(el('span', 'nx-card__line', work));
  if (m.city) card.appendChild(el('span', 'nx-card__line nx-card__muted', m.city));
  const meta = el('span', 'nx-card__meta');
  const seen = isOnline(m) ? 'Online now' : timeAgo(m.lastSeen);
  if (seen) meta.appendChild(el('span', '', isOnline(m) ? seen : `Seen ${seen}`));
  if (fresh) meta.appendChild(el('span', 'nx-card__badge', 'New'));
  card.appendChild(meta);
  const distance = el('span', 'nx-card__line nx-card__muted');
  card.appendChild(distance);

  pin.append(ring, el('span', 'nx-pin__tail'), card);
  pin.addEventListener('mouseenter', () => {
    const d = opts.distance?.() ?? null;
    distance.textContent = d ?? '';
    distance.style.display = d ? '' : 'none';
    placeCard(pin, card);
  });
  pin.addEventListener('click', (e) => {
    e.stopPropagation();
    opts.onClick();
  });
  return pin;
}

/* ---------- cluster ---------- */

/** Two faces stacked, with the count. Click zooms in. */
export function buildClusterPin(
  sample: MapMember[],
  count: number,
  onClick: () => void
): HTMLElement {
  const pin = el('button', 'nx-cluster');
  pin.type = 'button';
  pin.setAttribute('aria-label', `${count} members, zoom in`);
  const stack = el('span', 'nx-cluster__stack');
  sample.slice(0, 2).forEach((m) => stack.appendChild(face(m, 'nx-cluster__face')));
  stack.appendChild(el('span', 'nx-cluster__count', count > 999 ? '999+' : String(count)));
  pin.appendChild(stack);
  pin.addEventListener('click', (e) => {
    e.stopPropagation();
    onClick();
  });
  return pin;
}

/* ---------- space ---------- */

function spaceDates(s: MapSpace): string | null {
  if (!s.startDate) return null;
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return s.endDate && fmt(s.endDate) !== fmt(s.startDate)
    ? `${fmt(s.startDate)} – ${fmt(s.endDate)}`
    : fmt(s.startDate);
}

/** A space: its logo on a rounded tile with a member count. */
export function buildSpacePin(s: MapSpace, selected: boolean, onClick: () => void): HTMLElement {
  const pin = el('button', 'nx-space');
  pin.type = 'button';
  pin.setAttribute('aria-label', `${s.title}, ${s.memberIds.length} members`);
  if (selected) pin.classList.add('nx-space--selected');

  const tile = el('span', 'nx-space__tile');
  const initials = () => {
    tile.replaceChildren();
    tile.classList.add('nx-initials');
    tile.textContent = (s.title.trim()[0] ?? '?').toUpperCase();
  };
  if (s.logo) {
    const img = document.createElement('img');
    img.src = s.logo;
    img.alt = '';
    img.loading = 'lazy';
    img.addEventListener('error', initials, { once: true });
    tile.appendChild(img);
  } else {
    initials();
  }
  tile.appendChild(el('span', 'nx-space__count', String(s.memberIds.length)));

  const card = el('span', 'nx-card');
  card.appendChild(el('span', 'nx-card__name', s.title));
  const dates = spaceDates(s);
  if (dates) card.appendChild(el('span', 'nx-card__line nx-card__muted', dates));
  card.appendChild(
    el('span', 'nx-card__line', selected ? 'Showing its members · click for options' : `${s.memberIds.length} members · click for options`)
  );

  pin.append(tile, card);
  pin.addEventListener('mouseenter', () => placeCard(pin, card));
  pin.addEventListener('click', (e) => {
    e.stopPropagation();
    onClick();
  });
  return pin;
}

/* ---------- search point ---------- */

/** The point the admin clicked to measure distances from. */
export function buildSearchPin(): HTMLElement {
  const node = el('div', 'nx-search-pin');
  node.setAttribute('aria-hidden', 'true');
  node.append(el('span', 'nx-search-pin__pulse'), el('span', 'nx-search-pin__dot'));
  return node;
}

/*
 * Muted base map in the admin palette (styles.scss $primaryPalette): pale
 * land, teal water, no points of interest or transit, quiet labels. Photos
 * are the loudest thing on the map.
 */
export const MAP_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f1f3f3' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#657a78' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#eaeeed' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#bde2dd' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#1f6f66' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e3e8e7' }] },
  { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#909f9d' }, { weight: 0.8 }] },
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#d7dcdc' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#12332f' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#4a5f5c' }] },
];
